import React, { Fragment, useContext, useEffect, useRef, useState } from "react";
import { motion } from "framer-motion";
import toast from "react-hot-toast";
import { Card, Badge, Modal } from "react-bootstrap";
import ApiService from "../../../../services/ApiService";
import PageHeaderBar from "../../../layouts/PageHeaderBar";
import { epochToDateString, epochToDateTimeString } from "../../../../utils";
import { useDocumentTitle } from "../../../utils";
import { ThemeContext } from "../../../../context/ThemeContext";
import { useAuth } from "../../../Providers/LandmarkAuthContext";
import EmployeeInfo from "./EmployeeInfo";
import landmarkImg from "../../../../images/landmark/logo-text-dark.png";
import SalesNavImg from "../../../../images/misc/sales-nav-bot.png";

const SalesNavForm = ({ employee }) => {
  const [searchUrl, setSearchUrl] = useState("");
  const [searchTitle, setSearchTitle] = useState("");
  const [count, setCount] = useState("");
  const [deepScrape, setDeepScrape] = useState(false);
  const [stopOnRateLimit, setStopOnRateLimit] = useState(true);

  const [allTasks, setAllTasks] = useState([]);
  const [loadingTasks, setLoadingTasks] = useState(false);
  const [enrichEstimates, setEnrichEstimates] = useState({});
  const [loadingEnrichEstimates, setLoadingEnrichEstimates] = useState({});
  const [enrichRunning, setEnrichRunning] = useState({});
  const [downloadRunning, setDownloadRunning] = useState({});
  const [enrichModalOpen, setEnrichModalOpen] = useState(false);
  const [enrichModalTask, setEnrichModalTask] = useState(null);
  const [deleteModalOpen, setDeleteModalOpen] = useState(false);
  const [deleteModalTask, setDeleteModalTask] = useState(null);

  const [activeJobId, setActiveJobId] = useState("");
  const [streaming, setStreaming] = useState(false);
  const [logLines, setLogLines] = useState([]);
  const esControllerRef = useRef(null);
  const logRef = useRef(null);
  const streamEndedRef = useRef(false);

  const appendLog = (line) => {
    setLogLines((prev) => [...prev, line]);
  };

  const formatCurrency = (value = 0) => {
    const num = Number(value) || 0;
    return new Intl.NumberFormat("en-US", {
      style: "currency",
      currency: "USD",
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    }).format(num);
  };

  const copyToClipboard = async (value) => {
    if (!value) return;
    try {
      await navigator.clipboard.writeText(value);
      toast.success("Copied to clipboard");
    } catch (err) {
      console.error(err);
      toast.error("Failed to copy");
    }
  };

  const formatLogLine = (payload) => {
    if (!payload) return "";
    if (typeof payload === "string") return payload;
    const ts = payload.ts ? new Date(payload.ts).toLocaleTimeString() : "";
    const msg = payload.msg || JSON.stringify(payload);
    return ts ? `[${ts}] ${msg}` : msg;
  };

  useEffect(() => {
    const el = logRef.current;
    if (el) el.scrollTop = el.scrollHeight;
  }, [logLines]);

  useEffect(() => {
    return () => {
      if (esControllerRef.current) {
        esControllerRef.current.close?.();
        esControllerRef.current = null;
      }
    };
  }, []);

  const startStream = (api, jobId) => {
    if (!jobId) return;

    if (esControllerRef.current) {
      esControllerRef.current.close?.();
      esControllerRef.current = null;
    }

    setActiveJobId(jobId);
    setLogLines([]);
    setStreaming(true);
    streamEndedRef.current = false;

    const controller = api.openJobStream(jobId, {
      onOpen: (_ev, meta) => {
        appendLog(`SSE opened → ${meta?.url ?? ""}`);
      },
      onLog: (payload) => {
        appendLog(formatLogLine(payload));
      },
      onDone: (payload) => {
        appendLog(formatLogLine({ msg: "Job complete ✅", ts: payload?.ts }));
        if (payload?.fileUrl) {
          appendLog(`Download: ${payload.fileUrl}`);
        }
        streamEndedRef.current = true;
        setStreaming(false);
        esControllerRef.current = null;
        fetchAllTasks();
      },
      onError: (payload) => {
        if (payload?.error) {
          appendLog(formatLogLine({ msg: `Error: ${payload.error}` }));
        }
        if (!streamEndedRef.current && payload?.error) {
          appendLog(
            formatLogLine({ msg: "Stream closed (error or server finished)." })
          );
        }
        setStreaming(false);
        esControllerRef.current = null;
        fetchAllTasks();
      },
    });

    esControllerRef.current = controller;
  };

  const fetchAllTasks = async () => {
    try {
      setLoadingTasks(true);
      const api = new ApiService({ employee });
      const tasks = await api.get("landmark/linkedinmanager/getAllTasks");
      const sorted = Array.isArray(tasks)
        ? tasks.sort((a, b) => (b.createdTime || 0) - (a.createdTime || 0))
        : [];
      setAllTasks(sorted);
      fetchEnrichmentEstimates(sorted);
    } catch (error) {
      console.error(error);
    } finally {
      setLoadingTasks(false);
    }
  };

  useEffect(() => {
    fetchAllTasks();
  }, []);

  const isValidSalesNavUrl =
    searchUrl &&
    /^https?:\/\/(www\.)?linkedin\.com\/sales\//i.test(searchUrl.trim());

  const handleCreateTask = async () => {
    if (!isValidSalesNavUrl) {
      toast.error("Please enter a valid LinkedIn Sales Nav URL");
      return;
    }

    try {
      const api = new ApiService({ employee });
      const payload = {
        employee,
        requestorEmail: employee?.email,
        searchUrl,
        searchTitle,
        count: count ? Number(count) : 0,
        deepScrape,
        stopOnRateLimit,
      };

      const { success, error, taskId, jobId } = await api.post(
        "landmark/linkedinmanager/createTask",
        payload
      );

      if (!success) {
        toast.error(error || "Failed to create job");
        return;
      }

      toast.success(success, { duration: 6000 });
      fetchAllTasks();

      const streamId = jobId || taskId;
      if (streamId) {
        startStream(api, streamId);
      }
    } catch (err) {
      console.error(err);
      toast.error("Error starting Sales Nav scrape");
    }
  };

  const retryTask = async (taskId) => {
    if (!taskId) return;
    try {
      const api = new ApiService({ employee });
      const { success, error } = await api.post(
        "landmark/linkedinmanager/retryTask",
        { taskId }
      );
      if (!success) {
        toast.error(error || "Retry failed");
        return;
      }
      toast.success(success, { duration: 6000 });
      fetchAllTasks();
    } catch (err) {
      console.error(err);
      toast.error("Error retrying job");
    }
  };

  const resumeTask = async (taskId) => {
    if (!taskId) return;
    try {
      const api = new ApiService({ employee });
      const { success, error } = await api.post(
        "landmark/linkedinmanager/resumeTask",
        { taskId }
      );
      if (!success) {
        toast.error(error || "Resume failed");
        return;
      }
      toast.success(success, { duration: 6000 });
      fetchAllTasks();
      startStream(api, taskId);
    } catch (err) {
      console.error(err);
      toast.error("Error resuming job");
    }
  };

  const fetchEnrichmentEstimate = async (taskId) => {
    if (!taskId) return null;
    try {
      setLoadingEnrichEstimates((prev) => ({ ...prev, [taskId]: true }));
      const api = new ApiService({ employee });
      const res = await api.post(
        "landmark/linkedinmanager/getSalesNavEnrichmentEstimate",
        { taskId }
      );
      if (res?.success) {
        setEnrichEstimates((prev) => ({
          ...prev,
          [taskId]: res.meta || {},
        }));
        return res.meta || null;
      }
      if (res?.error) {
        console.warn(res.error);
      }
      return null;
    } catch (err) {
      console.error(err);
      return null;
    } finally {
      setLoadingEnrichEstimates((prev) => ({ ...prev, [taskId]: false }));
    }
  };

  const fetchEnrichmentEstimates = async (tasks = []) => {
    if (!tasks.length) return;
    const completedTasks = tasks.filter((t) => t?.completed && !t?.error);
    if (!completedTasks.length) return;
    await Promise.all(
      completedTasks.map((task) => fetchEnrichmentEstimate(task.taskId))
    );
  };

  const openEnrichModal = async (task) => {
    if (!task?.taskId) return;
    setEnrichModalTask(task);
    if (!enrichEstimates[task.taskId]) {
      await fetchEnrichmentEstimate(task.taskId);
    }
    setEnrichModalOpen(true);
  };

  const closeEnrichModal = () => {
    setEnrichModalOpen(false);
    setEnrichModalTask(null);
  };

  const openDeleteModal = (task) => {
    setDeleteModalTask(task);
    setDeleteModalOpen(true);
  };

  const closeDeleteModal = () => {
    setDeleteModalOpen(false);
    setDeleteModalTask(null);
  };

  const deleteTask = async () => {
    const taskId = deleteModalTask?.taskId;
    if (!taskId) return;
    try {
      const api = new ApiService({ employee });
      const res = await api.post("landmark/linkedinmanager/deleteTask", {
        taskId,
      });
      if (!res?.success) {
        toast.error(res?.error || "Failed to delete task");
        return;
      }
      toast.success(res.success);
      closeDeleteModal();
      fetchAllTasks();
    } catch (err) {
      console.error(err);
      toast.error("Error deleting task");
    }
  };

  const startEnrichment = async () => {
    const taskId = enrichModalTask?.taskId;
    if (!taskId) return;
    closeEnrichModal();
    try {
      setEnrichRunning((prev) => ({ ...prev, [taskId]: true }));
      const api = new ApiService({ employee });
      const res = await api.post(
        "landmark/linkedinmanager/enrichSalesNavTask",
        {
          taskId,
          includeEmail: true,
        },
        null,
        { timeout: 120000 },
      );
      if (!res?.success) {
        toast.error(res?.error || "Failed to start enrichment");
        return;
      }
      toast.success("Enrichment started");
      startStream(api, taskId);
      fetchAllTasks();
    } catch (err) {
      console.error(err);
      toast.error("Error starting enrichment");
    } finally {
      setEnrichRunning((prev) => ({ ...prev, [taskId]: false }));
    }
  };

  const downloadEnrichedCsv = async (taskId) => {
    if (!taskId) return;
    try {
      setDownloadRunning((prev) => ({ ...prev, [taskId]: true }));
      const api = new ApiService({ employee });
      const res = await api.post(
        "landmark/linkedinmanager/downloadSalesNavEnrichedCsv",
        { taskId }
      );
      if (!res?.success || !res?.fileUrl) {
        toast.error(res?.error || "No enriched CSV available");
        return;
      }
      window.open(res.fileUrl, "_blank");
    } catch (err) {
      console.error(err);
      toast.error("Error downloading enriched CSV");
    } finally {
      setDownloadRunning((prev) => ({ ...prev, [taskId]: false }));
    }
  };

  const watchTask = (taskId) => {
    try {
      const api = new ApiService({ employee });
      startStream(api, taskId);
    } catch (err) {
      console.error(err);
    }
  };

  return (
    <>
      <motion.div animate={{ x: 0 }} initial={{ x: 150 }}>
        <Card className="mb-3 h-fc bot-table sales-nav-card">
          <div className="logo-container">
            <img src={SalesNavImg} className="img-fluid" />
          </div>
          <div className="row">
            <div className="seamless-bot-form-wrapper">
              <div className="sub-logo-text-container">
                <div>
                  • Paste your Sales Navigator search URL
                  <br />
                  • Results are delivered as a CSV download link
                </div>
              </div>
              <div className="form-section text-left">
                <div className="form-group mb-3 col-12">
                  <label className="form-label">
                    Search Title <span className="example-text">(optional)</span>
                    <span className="example-text">
                      <br />
                      e.g. Chicago Execs
                    </span>
                  </label>
                  <input
                    type="text"
                    value={searchTitle}
                    className="form-control"
                    onChange={(e) => setSearchTitle(e.target.value)}
                  />
                </div>
                <div className="form-group mb-3 col-12">
                  <label className="form-label">
                    Sales Nav Search URL <span className="asterisk">*</span>
                    <span className="example-text">
                      <br />
                      e.g. https://www.linkedin.com/sales/search/people?query=...
                    </span>
                  </label>
                  <input
                    type="text"
                    value={searchUrl}
                    className="form-control"
                    onChange={(e) => setSearchUrl(e.target.value)}
                  />
                </div>
                <div className="form-group mb-3 col-12">
                  <label className="form-label">
                    Count <span className="example-text">(optional)</span>
                    <span className="example-text">
                      <br />
                      Total number of records required, will stop after this many records.
                    </span>
                  </label>
                  <input
                    type="number"
                    value={count}
                    className="form-control"
                    onChange={(e) => setCount(e.target.value)}
                  />
                </div>
                <div className="form-group mb-3 col-12">
                  <div className="form-check">
                    <input
                      type="checkbox"
                      className="form-check-input"
                      id="stopOnRateLimit"
                      checked={stopOnRateLimit}
                      onChange={(e) => setStopOnRateLimit(e.target.checked)}
                    />
                    <label className="form-check-label" htmlFor="stopOnRateLimit">
                      Stop On Rate Limit
                    </label>
                  </div>
                </div>
                <div className="d-flex justify-content-center align-items-center">
                  <button
                    className={`run-btn btn btn-primary ${
                      isValidSalesNavUrl ? "" : "disabled-soft"
                    }`}
                    onClick={handleCreateTask}
                  >
                    RUN SCRAPE
                    <i className="event-owner-add fa-sharp fa-solid fa-robot ml-05"></i>
                  </button>
                </div>
              </div>
            </div>
          </div>
        </Card>

        <div className="sales-nav-log-panel">
          <div className="sales-nav-log" ref={logRef}>
            {logLines.length ? (
              logLines.map((line, idx) => <div key={idx}>{line}</div>)
            ) : (
              <div className="text-muted">No logs yet.</div>
            )}
          </div>
        </div>

        <div className="sales-nav-section sales-nav-recent-jobs">
          <div className="section-header sales-nav-header-row">
            <span>Recent Jobs</span>
            <button
              className="sales-nav-refresh-btn icon-only"
              onClick={fetchAllTasks}
              disabled={loadingTasks}
              title="Refresh"
            >
              <i className="fa-solid fa-rotate-right" />
            </button>
          </div>
          <div className="section-body">
            {allTasks && allTasks.length ? (
              <div className="sales-nav-table-wrapper">
                <table className="sales-nav-table">
                  <thead>
                    <tr>
                      <th>Title</th>
                      <th className="col-search-url">Search URL</th>
                      <th>Requestor</th>
                      <th>Created</th>
                      <th>Profiles Scraped</th>
                      <th>File</th>
                      <th>Enrichment</th>
                      <th>Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {allTasks.map((task) => {
                      const {
                        taskId,
                        searchTitle,
                        searchUrl,
                        requestorEmail,
                        count,
                        resultsCount,
                        fileUrls,
                        completed,
                        error,
                        errorMsg,
                        createdTime,
                      } = task;

                      const fileUrl =
                        fileUrls && fileUrls.length ? fileUrls[0] : null;
                      const estimate = enrichEstimates[taskId];
                      const toEnrich = estimate?.toEnrich || 0;
                      const enrichedCount = estimate?.enrichedCount || 0;
                      const isEstimating = loadingEnrichEstimates[taskId];
                      const isEnriching = enrichRunning[taskId];
                      const isDownloading = downloadRunning[taskId];

                      const showWatch = !completed;
                      const showResume = completed && !error;
                      const showRerun = completed && !error;
                      const showRetry = !!error;
                      const showEnrichButton =
                        completed && !error && toEnrich > 0 && !isEnriching;

                      return (
                        <tr key={taskId}>
                          <td className="cell-wrap sales-nav-url-cell">
                            <div className="cell-title">
                              {searchTitle || "Sales Nav Search"}
                            </div>
                          </td>
                          <td className="cell-wrap">
                            <div className="sales-nav-url">
                              <a href={searchUrl} target="_blank" rel="noreferrer">
                                {searchUrl}
                              </a>
                              <button
                                type="button"
                                className="sales-nav-clip-btn"
                                onClick={() => copyToClipboard(searchUrl)}
                                title="Copy URL"
                              >
                                <i className="fa-regular fa-clipboard" />
                              </button>
                            </div>
                          </td>
                          <td className="cell-wrap">
                            {(requestorEmail || "").replace(/@landmarkventures\.com$/i, "")}
                          </td>
                          <td>
                            {epochToDateTimeString(createdTime)}
                          </td>
                          <td>
                            <div className="sales-nav-profiles-cell">
                              <span>{resultsCount || 0}</span>
                              {showResume ? (
                                <button
                                  type="button"
                                  className="sales-nav-text-btn"
                                  onClick={() => resumeTask(taskId)}
                                  title="Expecting more results? Attempt this run again starting on the page where we left off."
                                >
                                  Attempt more
                                </button>
                              ) : null}
                            </div>
                          </td>
                          <td className="cell-wrap">
                            {fileUrl ? (
                              <button
                                type="button"
                                className="sales-nav-link-btn"
                                onClick={() => window.open(fileUrl, "_blank")}
                              >
                                Download CSV
                              </button>
                            ) : (
                              <>
                                {error ? (
                                  <>
                                    <Badge bg="danger">Error</Badge>
                                    <div className="error-msg">{errorMsg}</div>
                                  </>
                                ) : completed ? (
                                  <Badge bg="success" className="sales-nav-pill-dark">
                                    Completed
                                  </Badge>
                                ) : (
                                  <Badge bg="warning">Queued</Badge>
                                )}
                              </>
                            )}
                          </td>
                          <td className="cell-wrap">
                            {isEnriching ? (
                              <div className="sales-nav-enriching-text">Enriching...</div>
                            ) : isEstimating ? (
                              <div className="text-muted">Checking…</div>
                            ) : showEnrichButton ? (
                              <button
                                type="button"
                                className="sales-nav-action-btn is-enrich"
                                onClick={() => openEnrichModal(task)}
                              >
                                Enrich {toEnrich} profiles
                              </button>
                            ) : enrichedCount > 0 ? null : (
                              <div className="text-muted">No enrichment needed</div>
                            )}
                            {enrichedCount > 0 ? (
                              <button
                                type="button"
                                className="sales-nav-link-btn is-enrich mt-1"
                                onClick={() => downloadEnrichedCsv(taskId)}
                                disabled={isDownloading}
                              >
                                {isDownloading ? "Preparing…" : "Download Enriched CSV"}
                              </button>
                            ) : null}
                          </td>
                          <td>
                            <div className="sales-nav-actions">
                              {showWatch ? (
                                <button
                                  className="sales-nav-action-btn is-secondary"
                                  onClick={() => watchTask(taskId)}
                                >
                                  Watch
                                </button>
                              ) : null}
                              {showRerun ? (
                                <button
                                  className="sales-nav-action-btn is-primary"
                                  onClick={() => retryTask(taskId)}
                                >
                                  Rerun
                                </button>
                              ) : null}
                              {showRetry ? (
                                <button
                                  className="sales-nav-action-btn is-primary"
                                  onClick={() => retryTask(taskId)}
                                >
                                  Retry
                                </button>
                              ) : null}
                              <button
                                type="button"
                                className="sales-nav-delete-btn"
                                onClick={() => openDeleteModal(task)}
                                title="Delete job"
                              >
                                <i className="fa-solid fa-trash" />
                              </button>
                            </div>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            ) : (
              <div className="text-muted">No tasks yet.</div>
            )}
          </div>
        </div>
        <Modal show={enrichModalOpen} onHide={closeEnrichModal} centered>
          <Modal.Header closeButton>
            <Modal.Title>Confirm Enrichment</Modal.Title>
          </Modal.Header>
          <Modal.Body>
            {enrichModalTask ? (
              <>
                <div className="mb-2">
                  Enriching will pull more detailed information from LinkedIn,
                  including emails when available, at a cost of $5 per 1000 profiles.
                </div>
                {enrichEstimates[enrichModalTask.taskId] ? (
                  <>
                    <div>
                      Profiles to enrich:{" "}
                      <b>{enrichEstimates[enrichModalTask.taskId].toEnrich || 0}</b>
                    </div>
                    <div>
                      Estimated cost:{" "}
                      <b>
                        {formatCurrency(
                          enrichEstimates[enrichModalTask.taskId].estimatedCost || 0
                        )}
                      </b>
                    </div>
                  </>
                ) : (
                  <div className="text-muted">Loading estimate…</div>
                )}
              </>
            ) : (
              <div className="text-muted">No task selected.</div>
            )}
          </Modal.Body>
          <Modal.Footer>
            <button className="sales-nav-modal-btn is-cancel" onClick={closeEnrichModal}>
              Cancel
            </button>
            <button
              className="sales-nav-modal-btn is-enrich"
              onClick={startEnrichment}
              disabled={
                !enrichModalTask ||
                !enrichEstimates[enrichModalTask.taskId] ||
                (enrichEstimates[enrichModalTask.taskId]?.toEnrich || 0) === 0
              }
            >
              Confirm Enrichment
            </button>
          </Modal.Footer>
        </Modal>
        <Modal show={deleteModalOpen} onHide={closeDeleteModal} centered>
          <Modal.Header closeButton>
            <Modal.Title>Delete Job?</Modal.Title>
          </Modal.Header>
          <Modal.Body>
            This will permanently delete the Sales Nav job record. This cannot be
            undone.
          </Modal.Body>
          <Modal.Footer>
            <button className="sales-nav-modal-btn is-neutral" onClick={closeDeleteModal}>
              Cancel
            </button>
            <button className="sales-nav-modal-btn is-cancel" onClick={deleteTask}>
              Delete Job
            </button>
          </Modal.Footer>
        </Modal>
      </motion.div>
    </>
  );
};

const Dashboard = () => {
  const { changeBackground } = useContext(ThemeContext);
  const { employee, isAuthenticated, loading, logout } = useAuth();

  useEffect(() => {
    changeBackground({ value: "light", label: "Light" });
  }, []);

  useEffect(() => {
    if (!isAuthenticated || !employee) return;
    try {
      const api = new ApiService({ employee });
      api.get("landmark/employees/login");
    } catch (error) {
      console.warn("employee login touch failed", error);
    }
  }, [isAuthenticated, employee]);

  useDocumentTitle("LinkedIn Sales Nav Scraper - Landmark Ventures");

  if (loading) {
    return (
      <div className="d-flex justify-content-center align-items-center p-5">
        <div className="spinner-border" role="status" aria-label="Loading" />
      </div>
    );
  }

  return (
    <Fragment>
      <div className="global-dash-wrapper seamless-bot sales-nav-wrapper">
        <div className="d-flex align-items-center justify-content-center">
          <img src={landmarkImg} className="img-fluid landmark-img" alt="Landmark" />
        </div>

        {isAuthenticated && employee && (
          <div className="landmark-sign-in" style={{ marginBottom: "18px" }}>
            <div className="existing-accounts-container">
              <EmployeeInfo
                landmarkEmployee={employee}
                disableHover={true}
                showLogout={true}
                logout={logout}
              />
            </div>
          </div>
        )}

        <PageHeaderBar
          pages={[
            { title: "Landmark", url: `` },
            { title: "LinkedIn Sales Nav Scraper", url: `/landmark/salesnavbot` },
          ]}
        />

        {isAuthenticated ? (
          <SalesNavForm employee={employee} />
        ) : (
          <div className="text-center text-muted p-5">
            Please sign in to access the tool.
          </div>
        )}
      </div>
    </Fragment>
  );
};

export default Dashboard;
